import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { Badge, Button, Card, CardBody, Col, Input, Label, Row, Table } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import api from '../../services/api-configuration';
import { getCustodyFileUrl } from '../devir-teslim/api';
import '../devir-teslim/devir-teslim.css';

// Veri Yükleme > Devralma (Açılış Stoku): the system starts empty; on day one the reality is uploaded and each car is born
// where it is — in stock at a zone of the chosen site (STOK), on board a voyage (GEMIDE, Sefer No), or on a carrier's
// truck (YOLDA, Taşıyıcı) — with a DEVRALMA record opening its chain of custody (the list is the evidence).
// Role group 'Veri Yükleme - Devralma' (038_takeover.sql).
const ROLE_GROUP = 40;
const STATE = { STOK: 'Stokta', GEMIDE: 'Gemide', YOLDA: 'Yolda (tır)' };
const TEMPLATE = [
    ['Şase', 'Marka', 'Car line', 'Tip', 'Durum', 'Bölge', 'Adres', 'Sorumlu Firma', 'Sefer No', 'Taşıyıcı',
        'Commercial denomination Code', 'Commercial Denomination name', 'Colour code', 'Colour name', 'Not'],
    ['VF3XXXXXXXXXXXXXX', 'PEUGEOT', '208', 'milli', 'STOK', 'STOK', '', '', '', '', '', '', '', 'BEYAZ', 'stok alanında'],
    ['VF3XXXXXXXXXXXXXY', 'PEUGEOT', '2008', 'milli', 'STOK', 'PERON', 'PP-1', '', '', '', '', '', '', '', 'grupaj şeridinde'],
    ['VF3XXXXXXXXXXXXXZ', 'PEUGEOT', '3008', 'ithal', 'GEMIDE', '', '', '', 'SHP2026000004', '', '', '', '', '', 'gemide'],
    ['VF3XXXXXXXXXXXXXW', 'PEUGEOT', '308', 'milli', 'YOLDA', '', '', '', '', 'HGE', '', '', '', '', 'bayiye giderken tırda'],
];

const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(TEMPLATE), 'Devralma');
    XLSX.writeFile(wb, 'devralma-acilis-stoku-sablon.xlsx');
};

const Results = ({ results }) => (
    <Table size="sm" className="mb-0">
        <thead><tr><th>Satır</th><th>Şase</th><th>Durum</th><th>Sonuç</th><th>Devir no</th><th>Açıklama</th></tr></thead>
        <tbody>{results.map((r) => (
            <tr key={r.line}><td>{r.line}</td><td>{r.chassis_no}</td><td>{STATE[r.state] || r.state}</td>
                <td>{r.ok ? <Badge color="success">doğdu</Badge> : <Badge color="danger">hata</Badge>}</td>
                <td>{r.handover_no || ''}</td><td>{r.message || ''}</td></tr>
        ))}</tbody>
    </Table>
);

const Devralma = () => {
    const { hasRole } = useAuth(ROLE_GROUP);
    const canEdit = hasRole('devralma_write');
    const [md, setMd] = useState({ sites: [], batches: [] });
    const [site, setSite] = useState('');
    const [at, setAt] = useState(moment().format('YYYY-MM-DDTHH:mm'));
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(null);
    const load = useCallback(() => api.post('/custody/takeovers', {}).then((r) => setMd(r.data))
        .catch((e) => toast.error(e?.response?.data?.error || 'Devralma bilgisi alınamadı')), []);
    useEffect(() => { load(); }, [load]);
    const zones = md.sites.find((s) => String(s.id) === site)?.zones || [];
    const run = async () => {
        setBusy(true);
        try {
            const form = new FormData();
            form.append('site_id', site || '0');
            form.append('snapshot_at', at);
            form.append('file', file, file.name);
            const res = (await api.post('/custody/takeover', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
            setResult(res);
            toast[res.failed ? 'warning' : 'success'](`${res.done} araç devralındı${res.failed ? `, ${res.failed} hata` : ''}`);
            setFile(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.error || 'Yüklenemedi'); }
        setBusy(false);
    };
    const download = async (b) => {
        try {
            const url = await getCustodyFileUrl(`list:${b.list_id}`);
            const a = document.createElement('a'); a.href = url; a.download = b.file_name; a.click();
        } catch (e) { toast.error('Liste indirilemedi'); }
    };
    return (
        <>
            <Breadcrumb parent="Veri Yükleme" current="Devralma (Açılış Stoku)" />
            <Card><CardBody>
                <div className="text-muted mb-3">
                    Sistem boş başlar. Devralma günü sahadaki gerçek durum yüklenir; her araç bulunduğu yerde doğar ve devir-teslim zinciri bir
                    <b> devralma kaydıyla</b> başlar (liste kanıt olarak saklanır). <b>STOK</b>: seçilen sahada Bölge (ve varsa Adres);
                    <b> GEMIDE</b>: Sefer No (Gemi Seferleri'nde açılmış sefer); <b>YOLDA</b>: Taşıyıcı firma. Aracı elinde tutan firma bölgenin
                    operatörüdür; farklıysa <b>Sorumlu Firma</b> kolonuna yazın. Yalnızca sistemde olmayan araçlar devralınır; bilinen hasarlar sonra
                    devir-teslim kontrolü veya "Belge ekle" ile kaydedilir.
                    <Button color="link" className="p-0 ml-1" onClick={downloadTemplate}>Şablonu indir</Button>
                </div>
                {canEdit && <div className="dt-draft mb-3">
                    <Row>
                        <Col md="4"><Label>Saha (STOK satırları için)</Label>
                            <Input type="select" value={site} onChange={(e) => setSite(e.target.value)}>
                                <option value="">— seçiniz —</option>
                                {md.sites.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                            </Input>
                            {site && <small className="text-muted">Bölgeler: {zones.join(', ') || 'işlevi tanımlı bölge yok'}</small>}</Col>
                        <Col md="3"><Label>Devralma (sayım) zamanı</Label>
                            <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} /></Col>
                        <Col md="5"><Label>Devralma listesi (.xlsx)</Label>
                            <Input type="file" accept=".xlsx" key={file ? 'f' : 'nf'} onChange={(e) => setFile(e.target.files[0] || null)} /></Col>
                    </Row>
                    <Button color="primary" className="mt-2" disabled={busy || !file || !at} onClick={run}>Devral</Button>
                </div>}
                {result && <div className="mb-3"><h6>Son yükleme</h6><Results results={result.results} /></div>}
                <h6>Devralma yüklemeleri</h6>
                <Table size="sm" hover responsive>
                    <thead><tr><th>Yükleme</th><th>Saha</th><th>Devralma zamanı</th><th>Liste</th><th>Doğan / hata</th><th>Kim</th></tr></thead>
                    <tbody>
                        {md.batches.length === 0 && <tr><td colSpan={6} className="text-muted">Henüz devralma yok.</td></tr>}
                        {md.batches.map((b) => (
                            <React.Fragment key={b.id}>
                                <tr onClick={() => setOpen(open === b.id ? null : b.id)} style={{ cursor: 'pointer' }}>
                                    <td>{moment(b.at).format('DD.MM.YYYY HH:mm')}</td><td>{b.site || '—'}</td>
                                    <td>{moment(b.snapshot_at).format('DD.MM.YYYY HH:mm')}</td>
                                    <td><Button size="sm" color="link" className="p-0" onClick={(e) => { e.stopPropagation(); download(b); }}>{b.file_name}</Button></td>
                                    <td>{b.done} / {b.failed}</td><td>{b.by}</td>
                                </tr>
                                {open === b.id && <tr><td colSpan={6}><Results results={b.results || []} /></td></tr>}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            </CardBody></Card>
        </>
    );
};

export default Devralma;
